import { PushNotificationChannel } from '../../api/ChatbotAPI';
import DB, { ChatbotMetainfo } from '../DB';
import type { WebPushSubscription } from '../../utils/WebPush';
import type { PurchaseInfo } from '../../api/ReplicantAPI';

export function createChatbotMetainfo({
    fetchMetainfos,
    writeChatbotMetainfo,
}: {
    fetchMetainfos: DB<any>['chatbot']['fetchMetainfos'];
    writeChatbotMetainfo: (
        id: string,
        action: (metainfo?: ChatbotMetainfo) => Promise<ChatbotMetainfo>,
    ) => Promise<ChatbotMetainfo>;
}): DB<any>['chatbot'] {
    return {
        fetchMetainfo: (id) => {
            return fetchMetainfos([id]).then((metainfos) => metainfos[id] ?? null);
        },
        fetchMetainfos,
        markSubscribed: async (id, subscribed, now) => {
            return writeChatbotMetainfo(id, async (chatbotMetainfo) =>
                markSubscribed(chatbotMetainfo ?? {}, subscribed, now),
            );
        },

        markSubscribedAndResetMessagingSession: async (id, subscribed, now) => {
            return writeChatbotMetainfo(id, async (chatbotMetainfo) =>
                resetMessagingSession(markSubscribed(chatbotMetainfo ?? {}, subscribed, now), now),
            );
        },

        markMessageSent: async (id, now) => {
            return writeChatbotMetainfo(id, async (chatbotMetainfo) => {
                if (!chatbotMetainfo) {
                    throw new Error('No chatbotMetainfo for id: ' + id);
                }

                chatbotMetainfo.messagesSinceLastSession = (chatbotMetainfo.messagesSinceLastSession || 0) + 1;
                chatbotMetainfo.timeLastMessageSent = now;

                return chatbotMetainfo;
            });
        },

        setInstagramMessengerId: ({ userId, instagramMessengerId }) =>
            writeChatbotMetainfo(userId, async (chatbotMetainfo) => ({
                ...chatbotMetainfo,
                instagramMessengerId,
            })),

        setLastReceivedMessagePayload: (userId, lastReceivedMessagePayload) =>
            writeChatbotMetainfo(userId, async (chatbotMetainfo) => ({
                ...chatbotMetainfo,
                lastReceivedMessagePayload,
            })),

        setMessengerId: async (id, msngrId) => {
            return writeChatbotMetainfo(id, async (chatbotMetainfo) => {
                if (!chatbotMetainfo) {
                    chatbotMetainfo = {
                        messengerId: msngrId,
                        hasUnsubscribed: false,
                        messagesSinceLastSession: 0,
                    };
                } else {
                    chatbotMetainfo.messengerId = msngrId;
                }

                return chatbotMetainfo;
            });
        },

        setSessionId: async (id, sessionId) => {
            return writeChatbotMetainfo(id, async (chatbotMetainfo) => {
                if (chatbotMetainfo) {
                    chatbotMetainfo.lastSessionId = sessionId;
                } else {
                    chatbotMetainfo = { lastSessionId: sessionId };
                }

                return chatbotMetainfo;
            });
        },

        resetMessagingSession: async (id, now: number) => {
            return writeChatbotMetainfo(id, async (chatbotMetainfo) =>
                resetMessagingSession(chatbotMetainfo ?? {}, now),
            );
        },

        setAppleDeviceToken: async (id: string, deviceToken: string | undefined, now: number) => {
            return writeChatbotMetainfo(id, async (chatbotMetainfo) => {
                if (!chatbotMetainfo) {
                    chatbotMetainfo = {};
                }

                if (deviceToken) {
                    chatbotMetainfo.appleDeviceToken = deviceToken;
                    chatbotMetainfo.appleDeviceTokenUpdatedAt = now;
                } else {
                    delete chatbotMetainfo.appleDeviceToken;
                    delete chatbotMetainfo.appleDeviceTokenUpdatedAt;
                }

                return chatbotMetainfo;
            });
        },

        setAndroidDeviceToken: async (id: string, deviceToken: string | undefined, now: number) => {
            return writeChatbotMetainfo(id, async (chatbotMetainfo) => {
                if (!chatbotMetainfo) {
                    chatbotMetainfo = {};
                }

                if (deviceToken) {
                    chatbotMetainfo.androidDeviceToken = deviceToken;
                    chatbotMetainfo.androidDeviceTokenUpdatedAt = now;
                } else {
                    delete chatbotMetainfo.androidDeviceToken;
                    delete chatbotMetainfo.androidDeviceTokenUpdatedAt;
                }

                return chatbotMetainfo;
            });
        },

        updateTimeLastTargetedByChannelMessage: async (id: string, channel: PushNotificationChannel, now: number) => {
            return writeChatbotMetainfo(id, async (chatbotMetainfo) => {
                if (!chatbotMetainfo) {
                    chatbotMetainfo = {
                        channelLastTargetedAt: { [channel]: now },
                    };
                } else {
                    chatbotMetainfo.channelLastTargetedAt = chatbotMetainfo.channelLastTargetedAt ?? {};
                    chatbotMetainfo.channelLastTargetedAt[channel] = now;
                }

                return chatbotMetainfo;
            });
        },

        setNativeBridgeSecret: async (id: string, bridgeSecret: string) => {
            return writeChatbotMetainfo(id, async (chatbotMetainfo) => {
                if (!chatbotMetainfo) {
                    chatbotMetainfo = {
                        nativeBridgeSecret: bridgeSecret,
                    };
                } else {
                    chatbotMetainfo.nativeBridgeSecret = bridgeSecret;
                }

                return chatbotMetainfo;
            });
        },

        setEmail: (id: string, email: string) =>
            writeChatbotMetainfo(id, async (chatbotMetainfo) => {
                if (!chatbotMetainfo) {
                    chatbotMetainfo = {};
                }
                chatbotMetainfo.email = email;
                return chatbotMetainfo;
            }),

        setWebPushSubscription: (id: string, pushSubscription: WebPushSubscription | undefined) =>
            writeChatbotMetainfo(id, async (chatbotMetainfo) => {
                if (!chatbotMetainfo) {
                    chatbotMetainfo = {};
                }
                chatbotMetainfo.webPushSubscription = pushSubscription;
                return chatbotMetainfo;
            }),

        subscribeSms: (id: string) =>
            writeChatbotMetainfo(id, async (chatbotMetainfo) => {
                if (!chatbotMetainfo) {
                    chatbotMetainfo = {};
                }
                chatbotMetainfo.smsSubscribed = true;

                return chatbotMetainfo;
            }),

        unsubscribeSms: (id: string) =>
            writeChatbotMetainfo(id, async (chatbotMetainfo) => {
                if (!chatbotMetainfo) {
                    chatbotMetainfo = {};
                }
                chatbotMetainfo.smsSubscribed = false;

                return chatbotMetainfo;
            }),

        awardPurchase: (id: string, purchase: PurchaseInfo) =>
            writeChatbotMetainfo(id, async (chatbotMetainfo) => {
                if (!chatbotMetainfo) {
                    chatbotMetainfo = {};
                }

                // Increment unconsumedProducts:
                const productId = purchase.product_id;
                const unconsumedProducts = chatbotMetainfo.unconsumedProducts ?? {};
                const available = unconsumedProducts[productId] ?? 0;
                unconsumedProducts[productId] = available + 1;
                chatbotMetainfo.unconsumedProducts = unconsumedProducts;

                // Add purchase to purchaseHistory:
                if (!chatbotMetainfo.purchaseHistory) {
                    chatbotMetainfo.purchaseHistory = [];
                }

                chatbotMetainfo.purchaseHistory.push(purchase);

                return chatbotMetainfo;
            }),

        consumePurchase: (id: string, productId: string, now: number) =>
            writeChatbotMetainfo(id, async (chatbotMetainfo) => {
                if (!chatbotMetainfo) {
                    chatbotMetainfo = {};
                }

                // Decrement unconsumedProducts:
                if (chatbotMetainfo.unconsumedProducts?.[productId]) {
                    chatbotMetainfo.unconsumedProducts[productId] -= 1;
                }

                // Mark purchase history item consumed:
                const purchaseHistoryItem = chatbotMetainfo.purchaseHistory?.find(
                    (item) => item.product_id === productId && !item.is_consumed,
                );
                if (purchaseHistoryItem) {
                    purchaseHistoryItem.is_consumed = true;
                    purchaseHistoryItem.issued_at = now;
                }

                return chatbotMetainfo;
            }),

        set: (id: string, callback: (oldChatbotMetainfo: ChatbotMetainfo) => void): Promise<ChatbotMetainfo> =>
            writeChatbotMetainfo(id, async (chatbotMetainfo) => {
                if (!chatbotMetainfo) {
                    chatbotMetainfo = {};
                }

                callback(chatbotMetainfo);

                return chatbotMetainfo;
            }),
    };
}

function markSubscribed(chatbotMetainfo: ChatbotMetainfo, subscribed: boolean, now: number): ChatbotMetainfo {
    const newChatbotMetainfo = { ...chatbotMetainfo, subscribed };

    if (subscribed && !chatbotMetainfo.timeFirstSubscribed) {
        newChatbotMetainfo.timeFirstSubscribed = now;
    }

    if (!subscribed && chatbotMetainfo.timeFirstSubscribed) {
        newChatbotMetainfo.hasUnsubscribed = true;
    }

    return newChatbotMetainfo;
}

function resetMessagingSession(chatbotMetainfo: ChatbotMetainfo, now: number): ChatbotMetainfo {
    return { ...chatbotMetainfo, messagesSinceLastSession: 0, timeLastSessionEnded: now };
}
